import React, { useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Chip,
  Collapse,
  Tooltip,
  Button,
  Stack,
  CircularProgress,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandAllIcon,
} from '@mui/icons-material';

const CategoryTreeNode = ({ 
  category, 
  level = 0, 
  expandedNodes, 
  onToggle, 
  loadedChildren,
  loadingNodes,
  onLoadChildren,
  onAddChild, 
  onEdit, 
  onView, 
  onDelete 
}) => {
  const categoryId = category.sid || category.id;
  const isExpanded = expandedNodes.has(categoryId);
  const hasChildren = category.has_children || (category.children_count > 0);
  const children = loadedChildren.get(categoryId) || [];
  const isLoading = loadingNodes.has(categoryId);
  const paddingLeft = level * 3;

  const handleToggle = async () => {
    if (!hasChildren) return;
    
    if (!isExpanded && children.length === 0) {
      await onLoadChildren(categoryId);
    }
    
    onToggle(categoryId);
  };

  return (
    <Box>
      {/* Category Row */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          py: 1.5,
          px: 2,
          pl: paddingLeft + 2,
          '&:hover': {
            backgroundColor: 'action.hover',
          },
          borderRadius: 1,
          mb: 0.5,
          minWidth: 0,
          overflow: 'hidden',
          flexWrap: { xs: 'wrap', sm: 'nowrap' },
          gap: { xs: 1, sm: 0 },
          minHeight: 48
        }}
      >
        {/* Expand/Collapse Icon */}
        {hasChildren ? (
          <Box sx={{ mr: 1, width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {isLoading ? (
              <CircularProgress size={16} />
            ) : (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleToggle();
                }}
                sx={{ 
                  transition: 'transform 0.2s',
                  transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                  width: 32,
                  height: 32,
                  p: 0
                }}
              >
                <ExpandMoreIcon />
              </IconButton>
            )}
          </Box>
        ) : null}
        
        {/* Spacer for leaf nodes to maintain alignment */}
        {!hasChildren ? (
          <Box sx={{ mr: 1, width: 32, height: 32 }} />
        ) : null}

        {/* Category Icon */}
        <Box
          sx={{
            mr: 2,
            fontSize: '1.5rem',
            minWidth: 32,
            textAlign: 'center',
          }}
        >
          {category.icon ? category.icon : '📦'}
        </Box>

        {/* Category Name */}
        <Box sx={{ 
          flex: 1, 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          minWidth: 0,
          overflow: 'hidden',
          mr: 1
        }}>
          <Typography 
            variant="body1" 
            sx={{ 
              fontWeight: level === 0 ? 'bold' : 'normal',
              flex: 1,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              color: 'text.primary',
              fontSize: { xs: '0.875rem', sm: '1rem' }
            }}
          >
            {category.name}
          </Typography>
          
          {/* Mobile Status Indicator */}
          <Box sx={{ 
            display: { xs: 'flex', sm: 'none' },
            alignItems: 'center',
            gap: 0.5,
            ml: 1
          }}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: category.is_active ? 'success.main' : 'grey.400'
              }}
            />
          </Box>
          
          {/* Status Badge */}
          <Chip
            label={category.is_active ? 'Active' : 'Inactive'}
            color={category.is_active ? 'success' : 'default'}
            size="small"
            sx={{ flexShrink: 0, display: { xs: 'none', sm: 'flex' } }}
          />
          
          {/* Featured Badge */}
          {category.is_featured && (
            <Chip 
              label="Featured" 
              color="primary" 
              size="small" 
              sx={{ flexShrink: 0, display: { xs: 'none', sm: 'flex' } }}
            />
          )}
          
          {/* Product Count */}
          <Chip
            label={`${category.products_count || 0} products`}
            variant="outlined"
            size="small"
            sx={{ flexShrink: 0, display: { xs: 'none', sm: 'flex' } }}
          />
        </Box>

        {/* Actions */}
        <Box sx={{ 
          display: 'flex', 
          gap: 0.5,
          flexShrink: 0,
          flexWrap: 'wrap',
          ml: { xs: 0, sm: 1 }
        }}>
          <Tooltip title="Add Child">
            <IconButton
              size="small"
              color="primary"
              onClick={() => onAddChild(category)}
              sx={{ minWidth: 'auto', p: 0.5 }}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          
          <Tooltip title="View Details">
            <IconButton
              size="small"
              color="info"
              onClick={() => onView(category)}
              sx={{ minWidth: 'auto', p: 0.5 }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          
          <Tooltip title="Edit">
            <IconButton
              size="small"
              color="secondary"
              onClick={() => onEdit(category)}
              sx={{ minWidth: 'auto', p: 0.5 }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          
          <Tooltip title="Delete">
            <IconButton
              size="small"
              color="error"
              onClick={() => onDelete(category)}
              sx={{ minWidth: 'auto', p: 0.5 }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Children */}
      {hasChildren && isExpanded && (
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <Box>
            {children.length > 0 ? (
              children.map((child) => (
                <CategoryTreeNode
                  key={child.sid || child.id || child.slug}
                  category={child}
                  level={level + 1}
                  expandedNodes={expandedNodes}
                  onToggle={onToggle}
                  loadedChildren={loadedChildren}
                  loadingNodes={loadingNodes}
                  onLoadChildren={onLoadChildren}
                  onAddChild={onAddChild}
                  onEdit={onEdit}
                  onView={onView}
                  onDelete={onDelete}
                />
              ))
            ) : (
              <Box sx={{ pl: paddingLeft + 6, py: 1, color: 'text.secondary' }}>
                <Typography variant="caption">No subcategories</Typography>
              </Box>
            )}
          </Box>
        </Collapse>
      )}
    </Box>
  );
};

const CategoryTree = ({ 
  categories, 
  loadedChildren,
  loadingNodes,
  onLoadChildren,
  onAddChild, 
  onEdit, 
  onView, 
  onDelete 
}) => {
  const [expandedNodes, setExpandedNodes] = useState(new Set());

  const toggleNode = (nodeId) => {
    setExpandedNodes(prevExpanded => {
      const newExpanded = new Set(prevExpanded);
      if (newExpanded.has(nodeId)) {
        newExpanded.delete(nodeId);
      } else {
        newExpanded.add(nodeId);
      }
      return newExpanded;
    });
  };

  const expandAll = () => {
    const allNodeIds = new Set();
    const collectNodeIds = (cats, level = 0) => {
      cats.forEach(cat => {
        if (cat.children && cat.children.length > 0) {
          allNodeIds.add(cat.sid || cat.id);
          collectNodeIds(cat.children, level + 1);
        }
      });
    };
    collectNodeIds(categories);
    setExpandedNodes(allNodeIds);
  };

  const collapseAll = () => {
    setExpandedNodes(new Set());
  };

  return (
    <Box>
      {/* Tree Controls */}
      <Stack direction="row" spacing={1} sx={{ mb: 2, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
        <Button
          size="small"
          startIcon={<ExpandAllIcon />}
          onClick={expandAll}
          variant="outlined"
        >
          Expand All
        </Button>
        <Button
          size="small"
          startIcon={<ExpandLessIcon />}
          onClick={collapseAll}
          variant="outlined"
        >
          Collapse All
        </Button>
      </Stack>

      {/* Category Tree */}
      {categories.map((category) => (
        <CategoryTreeNode
          key={category.sid || category.id || category.slug}
          category={category}
          level={0}
          expandedNodes={expandedNodes}
          onToggle={toggleNode}
          loadedChildren={loadedChildren}
          loadingNodes={loadingNodes}
          onLoadChildren={onLoadChildren}
          onAddChild={onAddChild}
          onEdit={onEdit}
          onView={onView}
          onDelete={onDelete}
        />
      ))}
    </Box>
  );
};

export default CategoryTree;

